import React, { useState, useEffect } from 'react';
import { User, Send, Code, Server, Terminal, Trophy, GraduationCap, Circle } from 'lucide-react';
import { SiPython, SiMariadb, SiArchlinux, SiDocker, SiGit, SiLinux } from 'react-icons/si';
import { FaJava } from 'react-icons/fa';
import './Profile.css';

const TEXTS = ["Backend Developer", "System Administrator", "Minecraft Engineer"];
const DISCORD_ID = "836647735809146930";

function Profile() {
  const [typedText, setTypedText] = useState('');
  const [textIndex, setTextIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [delay, setDelay] = useState(100);
  const [age, setAge] = useState('');
  const [dsData, setDsData] = useState(null);

  useEffect(() => {
    const fetchDS = async () => {
      try {
        const res = await fetch(`https://api.lanyard.rest/v1/users/${DISCORD_ID}`);
        const json = await res.json();
        if (json.success) setDsData(json.data);
      } catch (e) { console.error(e); }
    };
    fetchDS();
    const interval = setInterval(fetchDS, 10000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const birthDate = new Date('2010-07-02T00:00:00').getTime();
    const updateAge = () => {
      const now = new Date().getTime();
      const ageInYears = (now - birthDate) / 31557600000;
      setAge(ageInYears.toFixed(8));
    };
    const interval = setInterval(updateAge, 50);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const currentText = TEXTS[textIndex];
      if (!isDeleting) {
        setTypedText(currentText.substring(0, charIndex + 1));
        setCharIndex(charIndex + 1);
        if (charIndex === currentText.length) { setIsDeleting(true); setDelay(2000); }
      } else {
        setTypedText(currentText.substring(0, charIndex - 1));
        setCharIndex(charIndex - 1);
        if (charIndex === 0) { setIsDeleting(false); setTextIndex((textIndex + 1) % TEXTS.length); setDelay(500); }
      }
    }, isDeleting ? 40 : delay);
    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, textIndex, delay]);

  const avatarUrl = dsData?.discord_user
      ? `https://cdn.discordapp.com/avatars/${dsData.discord_user.id}/${dsData.discord_user.avatar}.png?size=256`
      : "/img/avatar-removebg4k.png";

  return (
      <div className="profile-container">
        <div className="profile-header">
          <User size={14} /> <span>about-me.md</span>
        </div>

        <div className="profile-content">
          <div className="profile-top-section">
            <div className="avatar-wrapper">
              <div className="avatar-glow" style={{ backgroundImage: `url(${avatarUrl})` }}></div>
              <img src={avatarUrl} alt="Avatar" className="photo-img" />
              <div className={`status-badge ${dsData?.discord_status || 'offline'}`}></div>
            </div>
            <div className="profile-intro">
              <h1 className="original-h1">Kxysl1k</h1>
              <div className="photo-container-hello">
                I am a {typedText}<span className="cursor">|</span>
              </div>
              <div className="age-counter">I am <span>{age}</span> years old</div>
            </div>
          </div>

          <div className="main-text-area">
            <p>Hello I am <strong>Kxysl1k</strong>, a backend developer, system administrator and Minecraft server engineer from Ukraine.</p>
            <p>My main work is with development using <strong>Java</strong> but I also like to use <strong>Python</strong> for automation and making bots and tools for servers.</p>
            <p>I really like to build systems that're stable and work well and I try to automate as much as I can. My goal is to make quality projects.</p>
            <p>My specialty is <strong>backend development setting up servers, system administration and automation.</strong></p>

            <hr className="profile-hr" />

            <h2 className="section-title"><Code size={18} className="icon-gap" /> Technologies</h2>
            <ul className="original-list">
              <li><FaJava /> Java</li>
              <li><SiPython /> Python</li>
              <li><SiMariadb /> MariaDB and MySQL</li>
              <li><Circle size={8} /> Paper API</li>
              <li><Circle size={8} /> Spigot API</li>
              <li><Circle size={8} /> Bukkit API</li>
              <li><SiDocker /> Docker, which I know a little about</li>
              <li><SiGit /> Git, which I know well</li>
              <li><SiLinux /> Linux</li>
            </ul>

            <hr className="profile-hr" />

            <h2 className="section-title"><Server size={18} className="icon-gap" /> Minecraft Servers</h2>
            <p>I have my own <strong>Minecraft server setup</strong>.</p>
            <p>I am good at:</p>
            <ul className="original-list">
              <li><Circle size={8} /> making plugins with <strong>Paper API</strong></li>
              <li><Circle size={8} /> working with <strong>Bukkit API</strong> and <strong>Spigot API</strong></li>
              <li><Circle size={8} /> making plugins for server cores like <strong>CatServer</strong> and <strong>Magma</strong></li>
              <li><Circle size={8} /> making Minecraft servers run faster and better</li>
              <li><Circle size={8} /> working with <strong>VPS</strong></li>
            </ul>
            <p>I really like to build server systems that are <strong>stable fast and can handle a lot of users.</strong></p>

            <hr className="profile-hr" />

            <h2 className="section-title"><Terminal size={18} className="icon-gap" /> Bots and Automation</h2>
            <p>I make bots for <strong>Discord and Telegram</strong> using <strong>Python and Java</strong>.</p>
            <p>Some things I make include:</p>
            <ul className="original-list">
              <li><Circle size={8} /> ticket systems</li>
              <li><Circle size={8} /> moderation tools</li>
              <li><Circle size={8} /> integration systems</li>
              <li><Circle size={8} /> automation tools</li>
              <li><Circle size={8} /> ways to connect bots to Minecraft servers</li>
            </ul>
            <p>I love automating tasks and making tools that make life easier for people.</p>

            <hr className="profile-hr" />

            <h2 className="section-title"><Trophy size={18} className="icon-gap" /> Achievements</h2>
            <p>I am participating in programming competitions and olympiads in my community and region for two years (2025-2026).</p>
            <p>I have won:</p>
            <ul className="original-list">
              <li><Circle size={8} /> <strong>first place in Java programming</strong></li>
              <li><Circle size={8} /> <strong>first place in the annual Python programming competition two years in a row</strong></li>
              <li><Circle size={8} /> <strong>the fourth place in a programming competition in Kyiv.</strong></li>
            </ul>
            <p>I also performed well in the <strong>Python Olympiad</strong>, where I took <strong>first place in the community and third place in the region.</strong></p>
            <p>I have been in computer graphics competitions, where I have won:</p>
            <ul className="original-list">
              <li><Circle size={8} /> <strong>place in 2D graphics two years in a row</strong></li>
              <li><Circle size={8} /> <strong>third place in 3D graphics two years in a row</strong></li>
            </ul>
            <p>I also enjoy creating simple games in Python, such as <strong>Limbo</strong> (first place in a programming contest). This is my favorite project so far.</p>
            <p>I got idea from this video: <a href="https://youtu.be/VEfAEsty5Pc?si=pAZn8gEsQ8VKy8Rq" target="_blank" rel="noopener noreferrer" className="limbo-link">video link</a></p>

            <hr className="profile-hr" />

            <h2 className="section-title"><GraduationCap size={18} className="icon-gap" /> Education and Interests</h2>
            <p>I am, in a special class where we learn <strong>advanced physics and math</strong>. I really like <strong>math, programming and how systems work</strong>.</p>
            <p>My main interests are <strong>backend development, server infrastructure and automation</strong>.</p>

            <hr className="profile-hr" />

            <div className="fun-fact">
              <span className="section-title">❖ Fun Fact</span>
              <div className="arch-btw">
                <SiArchlinux /> <span>I use Arch, btw.</span>
              </div>
            </div>
          </div>
        </div>

        <button className="projects-button" onClick={() => window.open('https://t.me/NO_PON_YES_ZROZ', '_blank')}>
          <Send size={18} /> Contact Me
        </button>
      </div>
  );
}

export default Profile;